// The two blocks that ride along with a marketing message, and the rules for
// where they land.
//
// They started life inside the Find Buyers composer. They live here now because
// Christian wants the same two lines on every page that sends (CG,
// 2026-08-28: "I really like these buttons though so please add it to both email
// pages" / "we like the Invite to the off market portal & let them update their
// preferences buttons that we want to add to both pages also"). One definition,
// four composers - Find Buyers email, Find Buyers text, Campaign email, Campaign
// text - so the wording can never drift between them again.
//
// A tick box each, EXCEPT on a message built from a property, where both are
// forced on and the agent cannot switch either off (CG, Monday 13056948901,
// 2026-09-16: "Sorry dont make it mandatory by default for emails & texts. Only
// make it mandatory if an agent populates from a property"). The composers own
// that rule - see MessageExtras `locked` - not this file.
//
// The two do different jobs:
//
//   Off Market Portal   for someone who is NOT with us yet. Links to
//                       collings.com.au/portal, where they can sign up.
//   Preferences         for a buyer we already have. A per-recipient, no-login
//                       link to change suburbs, price and beds.
//
// Both are merge tags resolved server side (communications.services.comms_engine
// .build_merge_context), so they work in a campaign body as well as a blast.

// ── the blocks ──────────────────────────────────────────────────────────────

// ── where an email block lands ──────────────────────────────────────────────
//
// MP, Monday 13056948901, 2026-09-17: "the portal sign up link and buyer
// preferences link looks like this at the bottom of the email ... Can we please
// fix it so it looks like the buttons made within the email maker ... And then
// it needs to be placed in between the signature (Kind Regards and the Carbeena
// Parade Button)."
//
// Both halves of that were the same fault. The blocks were being inserted
// before </body>, which on a branded Collings email is AFTER the white card
// closes - after the sign-off, after the compliance panel, after the grey
// social bar - so they rendered as two bare underlined links floating on the
// page background with none of the email's styling on them.
//
// So the templates now mark the one spot the blocks belong: the end of the body
// row, under the agent's copy and its CTA, above "Kind regards". Kept as an
// HTML comment because every builder, minifier and email client leaves one
// alone, and because it is invisible if a body ever ships without the blocks.
// Written by core.services.email_templates._footer_html (every BE-rendered
// email, including a GeeVee campaign) and by propertyEmail.js (Just Listed).
// Both sides hold the same literal - it is a wire format between them, so it
// cannot be renamed on one side only.
export const EXTRAS_ANCHOR = '<!--COLLINGS_EMAIL_EXTRAS-->';

// Each block is fenced by a comment pair. Everything downstream that has to
// find a block again - taking it back off a saved campaign body here, dropping
// the off-market invitation for someone already on the portal in
// communications.services.portal_membership - matches the fence, not the
// wording inside it. The wording has been rewritten three times in a month; the
// fence has to outlive the next rewrite.
export const OFF_MARKET_FENCE = ['<!--CG:off-market-->', '<!--/CG:off-market-->'];
export const PREFERENCES_FENCE = ['<!--CG:preferences-->', '<!--/CG:preferences-->'];
export const UNSUBSCRIBE_FENCE = ['<!--CG:unsubscribe-->', '<!--/CG:unsubscribe-->'];

// ── the blocks ──────────────────────────────────────────────────────────────
//
// Brand values mirror core.services.email_templates (CTA_DARK, FONT_STACK) so a
// block sits in the same type and colour as the email around it.
//
// There is no pill helper here any more, and that is deliberate - see
// OFF_MARKET_SENTENCE_HTML. The ONLY button in a Collings marketing email is
// now the agent's own property CTA, which the template draws
// (email_templates.email_button). Anything that puts a second one back has
// undone the 21 Sep change.
const CTA_DARK = '#0F172A';
const TEXT_PRIMARY = '#1F2937';
const FONT_STACK = "-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif";

const blockLead = (html) => (
  '<p style="margin:22px 0 0;text-align:center;font-family:' + FONT_STACK + ';'
  + 'font-size:14px;line-height:1.6;color:' + TEXT_PRIMARY + ';">' + html + '</p>'
);

// An inline link inside one of those sentences.
//
// Coloured CTA_DARK rather than the brand teal: the teal is #2DC8DB, which is
// fine as capitals on a navy pill and close to unreadable as 14px body text on
// white. Navy keeps it the same colour as the property button above it, so the
// blocks still read as part of the same email, and the underline plus the
// weight - not the colour - is what says "this is clickable". That matters
// because Outlook and Gmail both re-colour anchors they do not like, and
// neither touches the rule.
const blockLink = (label, href) => (
  '<a href="' + href + '"'
  + ' style="color:' + CTA_DARK + ';font-weight:600;text-decoration:underline;">'
  + label + '</a>'
);

// ── why neither of these is a button ────────────────────────────────────────
//
// MP, WhatsApp, 21 Sep 2026, looking at the two pills the 18 Sep change put
// under the agent's own property CTA: "Hmm ... When I see it like that ... I
// know it's a short button but it's just a lot. Maybe the better alternative is
// to make it a hyperlink? for updating the preferences." Asked about the other
// one he said to leave it as a button; Collings then asked for both, so the
// property CTA is the only button left in the email. If that comes back for
// discussion, THIS is the line that was overruled, not Michael's first note.
//
// The reason it reads better either way: the agent's property button is the
// click the email is for. Stacking house-style pills under it made three equal
// shouts and the property lost. These two blocks are standing offers, not the
// point of the message, so they belong in the prose.
//
// Christian dictated both sentences word for word (CG, Monday 13056948901,
// 2026-09-16), and both are now back in one piece with the link on the words he
// linked - which is how he wrote them in the first place. Promoting a phrase
// into a shouted label was only ever a consequence of it being a button.
//
// Split either side of the linked phrase so the sentence the agent is shown and
// the sentence that sends are built from the same pieces and cannot drift apart
// the way the SMS and email wordings once did.
const OFF_MARKET_BEFORE = 'Not on our Off Market Portal yet? ';
const OFF_MARKET_AFTER =
  ' and we will let you know about properties before they hit the market.';
export const OFF_MARKET_LINK_LABEL = 'Join the Collings platform';

export const OFF_MARKET_LEAD_HTML =
  OFF_MARKET_BEFORE + OFF_MARKET_LINK_LABEL + OFF_MARKET_AFTER;

// Same treatment, same reasons. See the block above.
const PREFERENCES_BEFORE = 'Not quite what you are after? ';
const PREFERENCES_AFTER = ' and we will only send you properties that match.';
export const PREFERENCES_LINK_LABEL = 'Update your property preferences';

export const PREFERENCES_LEAD_HTML =
  PREFERENCES_BEFORE + PREFERENCES_LINK_LABEL + PREFERENCES_AFTER;

export const OFF_MARKET_SENTENCE_HTML =
  OFF_MARKET_FENCE[0]
  + blockLead(
    OFF_MARKET_BEFORE
    + blockLink(OFF_MARKET_LINK_LABEL, '{{OffMarketPortalLink}}')
    + OFF_MARKET_AFTER,
  )
  + OFF_MARKET_FENCE[1];

// A text has no anchor tags, so the link has to be spelled out at the end of the
// sentence rather than wrapped around words in the middle of it (CG, Monday
// 13056948901, 2026-09-16). Unchanged by the button work - there is no such
// thing as a button in an SMS.
export const OFF_MARKET_SENTENCE_SMS =
  'Not on our Off Market Portal yet? Join our platform and we will let you know '
  + 'about properties before they hit the market: {{OffMarketPortalLink}}';

// What the composer shows the agent under the message box. No brackets on
// either of these now: there is no button to draw, and each already reads as a
// sentence.
export const OFF_MARKET_SENTENCE_PLAIN = OFF_MARKET_LEAD_HTML;

export const PREFERENCES_SENTENCE_HTML =
  PREFERENCES_FENCE[0]
  + blockLead(
    PREFERENCES_BEFORE
    + blockLink(PREFERENCES_LINK_LABEL, '{{PreferencesLink}}')
    + PREFERENCES_AFTER,
  )
  + PREFERENCES_FENCE[1];

export const PREFERENCES_SENTENCE_SMS =
  'Not quite what you are after? Update your property preferences and we will '
  + 'only send you properties that match: {{PreferencesLink}}';

export const PREFERENCES_SENTENCE_PLAIN = PREFERENCES_LEAD_HTML;

export const LEGACY_OFF_MARKET_SMS = [
  'Join our Off Market Portal to hear about properties first: {{OffMarketPortalLink}}',
];

export const LEGACY_PREFERENCES_SMS = [
  'Not quite right? Update what you are looking for: {{PreferencesLink}}',
];

// Both pre-fence wordings, the 26 Aug one and the 16 Sep one. A campaign saved
// between 16 and 17 Sep carries the second of these in its body, so without it
// here the agent re-opens a draft, the old bare link stays put and the new
// block is added underneath it.
//
// The 18-21 Sep pill version is NOT listed here and does not need to be: it
// shipped inside a fence, and splitEmailBlocks cuts fence to fence without
// reading what is between them. That is exactly what the fences were added
// for, and it is why this list stopped growing with the last two rewrites.
export const LEGACY_OFF_MARKET_HTML = [
  '<p style="margin:24px 0 0">Not on our Off Market Portal yet? '
  + '<a href="{{OffMarketPortalLink}}" style="color:#4f46e5">Join the Collings platform</a> '
  + 'and we will let you know about properties before they hit the market.</p>',
  '<p style="margin:24px 0 0">Not on our Off Market Portal yet? '
  + '<a href="{{OffMarketPortalLink}}" style="color:#4f46e5">Join the Collings Off Market Portal</a> '
  + 'and we will let you know about properties before they hit the market.</p>',
];

export const LEGACY_PREFERENCES_HTML = [
  '<p style="margin:24px 0 0">Not quite what you are after? '
  + '<a href="{{PreferencesLink}}" style="color:#4f46e5">Update your property preferences</a> '
  + 'and we will only send you properties that match.</p>',
  '<p style="margin:24px 0 0">Not quite what you are after? '
  + '<a href="{{PreferencesLink}}" style="color:#4f46e5">Update your property preferences</a> '
  + 'and we will only send you properties that match. No login needed.</p>',
];

// The per-recipient unsubscribe link is only known at send time, so the composer
// carries a placeholder the backend swaps (comms_engine._send_email). Kept as a
// quiet outline pill, not a brand button: it now sits inside the card with the
// other two and must not compete with them.
export const UNSUBSCRIBE_BUTTON_HTML =
  UNSUBSCRIBE_FENCE[0]
  + '<p style="margin:22px 0 0;text-align:center">'
  + '<a href="%%UNSUBSCRIBE_URL%%" style="display:inline-block;padding:8px 18px;border:1px solid #d1d5db;'
  + 'border-radius:6px;color:#6b7280;font-size:12px;text-decoration:none">Unsubscribe</a></p>'
  + UNSUBSCRIBE_FENCE[1];

export const LEGACY_UNSUBSCRIBE_HTML = [
  '<p style="margin:28px 0 0;text-align:center">'
  + '<a href="%%UNSUBSCRIBE_URL%%" style="display:inline-block;padding:8px 18px;border:1px solid #d1d5db;'
  + 'border-radius:6px;color:#6b7280;font-size:12px;text-decoration:none">Unsubscribe</a></p>',
];

// Markers so a block already appended to a body can be found and taken back out
// when its tick box is unticked - a campaign body is saved and re-opened, so the
// composer cannot rely on assembling from scratch every time the way a one-shot
// modal can.
export const OFF_MARKET_TAG = '{{OffMarketPortalLink}}';
export const PREFERENCES_TAG = '{{PreferencesLink}}';

// ── assembling a message ────────────────────────────────────────────────────

// CG, 2026-08-28: "Please space out the sentences. Its all to cludded together.
// For each button make them start on there own line and space them all out from
// the other lines like its done with Unsubscribe currently." So a text joins its
// blocks with a blank line, exactly like the opt-out clause the provider appends
// ("\n\nUnsubscribe: ..."), instead of running them together with a space.
export const BLOCK_SEPARATOR_SMS = '\n\n';

/** The text as it will be sent: the agent's words, then each ticked block on
 *  its own line, blank line between. */
export function assembleSms(body, { offMarket = false, preferences = false } = {}) {
  const parts = [(body || '').trim()];
  if (offMarket) parts.push(OFF_MARKET_SENTENCE_SMS);
  if (preferences) parts.push(PREFERENCES_SENTENCE_SMS);
  return parts.filter(Boolean).join(BLOCK_SEPARATOR_SMS);
}

/** The blocks themselves, in order, with nothing wrapped around them. */
export function emailBlocksHtml({ offMarket = false, preferences = false, unsubscribe = true } = {}) {
  const parts = [];
  if (offMarket) parts.push(OFF_MARKET_SENTENCE_HTML);
  if (preferences) parts.push(PREFERENCES_SENTENCE_HTML);
  if (unsubscribe) parts.push(UNSUBSCRIBE_BUTTON_HTML);
  return parts.join('');
}

// At the anchor the blocks are landing between two <tr>s of the card's own
// table, so they have to BE a row - a loose <p> or <table> dropped between rows
// is invalid and every renderer hoists it out of the table, which is a longer
// way of arriving back at the bug we are fixing. Padding matches the body row
// above it (36px, 22px on a phone via the template's .px rule) so the buttons
// line up with the copy, and the white background continues the card.
const asCardRow = (blocks) => (
  '<tr><td class="px" align="center" style="padding:0 36px 26px;background:#ffffff;">'
  + blocks
  + '</td></tr>'
);

// Nowhere to anchor - a designer-built email with its own scaffolding. Same
// blocks, but standing on their own, so they need their own table to be valid
// where they land.
const asLooseTable = (blocks) => (
  '<table role="presentation" width="100%" border="0" cellpadding="0" cellspacing="0">'
  + '<tr><td align="center" style="padding:8px 24px 24px;">'
  + blocks
  + '</td></tr></table>'
);

/**
 * Put the blocks into an email body that may be a whole HTML document.
 *
 * First choice is the anchor the Collings templates leave at the end of the
 * body row, so the blocks land under the agent's copy and its CTA and above
 * "Kind regards" - inside the white card, carrying the card's styling (MP,
 * Monday 13056948901, 2026-09-17). See EXTRAS_ANCHOR above for why that was
 * the whole complaint.
 *
 * Failing that, before </body> or </html> as before. An email built in the
 * drag-and-drop designer is the agent's own scaffolding with no anchor in it,
 * and appending after </html> is not safe - a strict client drops anything
 * outside the document, which is exactly how the "always automatic"
 * unsubscribe went invisible before (comms_engine._send_email has the same
 * guard).
 */
export function appendEmailBlocks(html, opts = {}) {
  const blocks = emailBlocksHtml(opts);
  if (!blocks) return html || '';
  const body = html || '';

  const anchor = body.indexOf(EXTRAS_ANCHOR);
  if (anchor !== -1) {
    // A body carrying the anchor is a Collings template, so it already has a
    // working per-recipient unsubscribe a few centimetres below this, in the
    // compliance panel ("Unsubscribe from marketing emails" - the same
    // %%UNSUBSCRIBE_URL%%). Adding the pill as well put two of them one under
    // the other, which was invisible while the pill was floating off the bottom
    // of the page and obvious the moment it came inside the card. The
    // guarantee is untouched: there is still exactly one, and it is still
    // automatic.
    const inCard = emailBlocksHtml({ ...opts, unsubscribe: false });
    if (!inCard) return body;
    return body.slice(0, anchor) + asCardRow(inCard) + body.slice(anchor);
  }

  const loose = asLooseTable(blocks);
  for (const tag of ['</body>', '</html>']) {
    const at = body.toLowerCase().lastIndexOf(tag);
    if (at !== -1) return body.slice(0, at) + loose + body.slice(at);
  }
  return body + loose;
}

// ── previewing and pricing ──────────────────────────────────────────────────

// Merge tags and the unsubscribe placeholder are only resolved by the backend at
// send time. Left raw, the agent sees {{tags}} and a dead %%UNSUBSCRIBE_URL%%
// and learns nothing about how the message actually reads.
//
// The links are shown at the length they will really be: since 28 Aug the
// backend shortens every long link in a text to /l/<code>
// (communications.services.link_shortener), which is what took the preferences
// link from ~290 characters down to 37.
export const SHORT_LINK_SAMPLE = 'crm.collings.com.au/l/Ab3kZ9m';

export const previewText = (text, firstName) => (text || '')
  .replace(/\{\{\s*FirstName\s*\}\}/g, firstName || 'there')
  .replace(/\{\{\s*Name\s*\}\}/g, firstName || 'there')
  .replace(/\{\{\s*OffMarketPortalLink\s*\}\}/g, 'collings.com.au/portal')
  .replace(/\{\{\s*PreferencesLink\s*\}\}/g, SHORT_LINK_SAMPLE);

// The recipient's name comes from their own sign-up, so it is escaped before
// it goes into preview HTML: a name like <img onerror=...> would otherwise run
// in the agent's CRM session.
export const escapeHtml = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

export const previewHtml = (html, firstName) => (html || '')
  .replace(/\{\{\s*FirstName\s*\}\}/g, escapeHtml(firstName || 'there'))
  .replace(/\{\{\s*Name\s*\}\}/g, escapeHtml(firstName || 'there'))
  .replace(/\{\{\s*OffMarketPortalLink\s*\}\}/g, '#')
  .replace(/\{\{\s*PreferencesLink\s*\}\}/g, '#')
  .replace(/%%UNSUBSCRIBE_URL%%/g, '#');

// Stand-ins used only to PRICE a message. A merge tag is nothing like the value
// it becomes: {{OffMarketPortalLink}} is 24 characters that turn into a real
// URL, and {{PreferencesLink}} into a signed-JWT link several times longer
// again. These are the shapes the backend actually produces
// (buyer_portal.services.find_buyers) BEFORE tracking and shortening - smsCredits
// .asSent then models those two passes in the same order the backend runs them,
// so the quote matches the bill.
export const PRICING_STANDINS = {
  FirstName: 'Samantha',
  Name: 'Samantha Nguyen',
  OffMarketPortalLink: 'https://collings.com.au/portal',
  PreferencesLink:
    'https://crm.collings.com.au/buyer-portal/preferences/?token=' + 'x'.repeat(230),
};

export const pricedText = (text) => (text || '').replace(
  /\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g,
  (whole, tag) => (Object.prototype.hasOwnProperty.call(PRICING_STANDINS, tag)
    ? PRICING_STANDINS[tag] : whole),
);

// ── saved bodies (Campaign Manager) ─────────────────────────────────────────
//
// A blast composer assembles its message once and throws it away. A campaign is
// saved, re-opened and edited, so the blocks have to come back OFF the saved
// body when the editor loads - otherwise the agent is typing around them, the
// tick boxes read as unticked next to lines that are plainly there, and the
// next save appends a second copy.
//
// Split on load, re-apply on save. The blocks are exact constants, so this is a
// string match, not a parse: an agent who hand-edits the raw HTML in the middle
// of one just keeps it as ordinary body copy, and the tag check stops it being
// added twice.

const stripSuffix = (text, suffix, separator = '') => {
  const whole = separator + suffix;
  if (text.endsWith(whole)) return text.slice(0, -whole.length);
  if (text.endsWith(suffix)) return text.slice(0, -suffix.length);
  return text;
};

/** Take the appended blocks back off a saved SMS body. Current wording first,
 *  then every wording we have shipped before, so a draft saved under the old
 *  sentences is cleaned out instead of keeping them alongside the new ones. */
export function splitSmsBlocks(body) {
  let rest = body || '';
  let preferences = false;
  let offMarket = false;
  const stripAny = (variants) => {
    for (const variant of variants) {
      const without = stripSuffix(rest, variant, BLOCK_SEPARATOR_SMS);
      if (without !== rest) { rest = without; return true; }
    }
    return false;
  };
  // Stripped in reverse of the order they were added.
  preferences = stripAny([PREFERENCES_SENTENCE_SMS, ...LEGACY_PREFERENCES_SMS]);
  offMarket = stripAny([OFF_MARKET_SENTENCE_SMS, ...LEGACY_OFF_MARKET_SMS]);
  return { body: rest, offMarket, preferences };
}

/** Cut everything between a fence pair, the pair itself included. Returns null
 *  when the fence is not there, so the caller can fall back to the exact-string
 *  wordings we shipped before fences existed. */
function dropFenced(html, [open, close]) {
  const from = html.lastIndexOf(open);
  if (from === -1) return null;
  const to = html.indexOf(close, from);
  if (to === -1) return null;
  return html.slice(0, from) + html.slice(to + close.length);
}

/** Take the appended blocks back off a saved email body, plus the empty card
 *  row they were sitting in, so re-opening a draft gives the agent their own
 *  copy and nothing else. */
export function splitEmailBlocks(html) {
  let rest = html || '';
  const drop = (needle) => {
    const at = rest.lastIndexOf(needle);
    if (at === -1) return false;
    rest = rest.slice(0, at) + rest.slice(at + needle.length);
    return true;
  };
  const dropAny = (fence, variants) => {
    const fenced = dropFenced(rest, fence);
    if (fenced !== null) { rest = fenced; return true; }
    return variants.some(drop);
  };

  const preferences = dropAny(PREFERENCES_FENCE,
    [PREFERENCES_SENTENCE_HTML, ...LEGACY_PREFERENCES_HTML]);
  const offMarket = dropAny(OFF_MARKET_FENCE,
    [OFF_MARKET_SENTENCE_HTML, ...LEGACY_OFF_MARKET_HTML]);
  dropAny(UNSUBSCRIBE_FENCE, [UNSUBSCRIBE_BUTTON_HTML, ...LEGACY_UNSUBSCRIBE_HTML]);

  // The wrapper the three blocks were put in. Only ever removed once it is
  // empty, so a row the agent has since dropped something of their own into is
  // left exactly where it is.
  rest = rest.replace(
    /<tr><td class="px" align="center" style="padding:0 36px 26px;background:#ffffff;">\s*<\/td><\/tr>/g,
    '',
  ).replace(
    /<table role="presentation" width="100%" border="0" cellpadding="0" cellspacing="0"><tr><td align="center" style="padding:8px 24px 24px;">\s*<\/td><\/tr><\/table>/g,
    '',
  );

  return { body: rest, offMarket, preferences };
}

// ── the agent contact block (SMS) ───────────────────────────────────────────
//
// Recipients can't reply to a broadcast text - it goes out from the fixed
// COLLINGS alphanumeric sender - so the message has to say who to call. Kept on
// its own two lines and prefixed with a marker so it can be found and replaced
// cleanly when the agent is switched. Shared by the Campaign composer and the
// Find Buyers text composer, which Christian wants working the same way (CG,
// 2026-08-28: "We need to make the 2 text platforms conistent").

export const SMS_SIG_MARKER = BLOCK_SEPARATOR_SMS + "- ";

export function buildSmsAgentBlock(agent) {
  if (!agent) return "";
  const phone = (agent.phone_number || "").trim();
  const name = (agent.name || "your Collings agent").trim();
  return `${SMS_SIG_MARKER}${name}, Collings Real Estate${phone ? `. Call ${phone}` : ""}`;
}

/** Strip a previously-inserted agent block from an SMS body. The block is
 *  always appended last, so we cut from the LAST marker to avoid touching any
 *  earlier "- " the user typed themselves. */
export function stripSmsAgentBlock(body) {
  if (!body) return "";
  const i = body.lastIndexOf(SMS_SIG_MARKER);
  return i === -1 ? body : body.slice(0, i);
}
